// Single rule for order amounts (see dev order/list payload: fullSum, resultSum, loyaltyBonus, discounts[]):
//   gross    = fullSum (server value incl. modifiers), else Σ price × qty of items and their modifiers
//   bonus    = loyaltyBonus. The same bonus is also listed in discounts[] as discountType 'Qbeek' rows,
//              so it is taken out of the discount sum (only up to the bonus; other Qbeek rows are real discounts)
//   net      = resultSum when it is > 0, else max(0, gross − discount − bonus)
//              (server resultSum 0 on a non-empty order is an unsettled/legacy value, not "free")
//   discount = gross − net − bonus when the server net is known (so gross − discount − bonus = net always holds),
//              else Σ discounts[].discountValue − min(bonus, Σ Qbeek rows)
const num = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};
const cents = (value) => Math.round((value + Number.EPSILON) * 100) / 100;
const firstDefined = (...values) => values.find((value) => value != null);
const firstPositive = (...values) => values.map(num).find((n) => n > 0);

const BONUS_KEYS = ['bonusWriteOffAmount', 'appliedBonus', 'writeOffBonus', 'bonusSpent', 'bonusUsed', 'loyaltyBonus'];

function sumLines(order) {
  const lines = Array.isArray(order?.orderItems) ? order.orderItems : Array.isArray(order?.items) ? order.items : [];
  return lines.reduce((sum, line) => {
    const own = num(line?.productPrice ?? line?.itemPrice ?? line?.price) * num(line?.qty ?? line?.quantity ?? line?.count);
    const modifiers = (Array.isArray(line?.modifiers) ? line.modifiers : []).reduce(
      (s, m) => s + num(m?.productPrice) * num(m?.qty),
      0,
    );
    return sum + own + modifiers;
  }, 0);
}

export function getOrderAmounts(order = {}) {
  const gross = cents(
    firstPositive(order?.fullSum, order?.orderSum, order?.grossAmount, order?.totalPrice, order?.priceBeforeDiscounts)
      ?? sumLines(order),
  );
  const bonus = cents(num(firstDefined(...BONUS_KEYS.map((key) => order?.[key]))));
  const serverNet = firstPositive(order?.netAmount, order?.resultSum, order?.amountToPaid);

  let discount;
  if (serverNet != null && gross > 0) {
    // derived from the server total, so rows always add up (also when the server applied discounts not listed in discounts[])
    discount = cents(Math.max(0, gross - serverNet - bonus));
  } else if (Array.isArray(order?.discounts)) {
    const value = (d) => num(d?.discountValue ?? d?.value ?? d?.amount);
    const all = order.discounts.reduce((sum, d) => sum + value(d), 0);
    const loyalty = order.discounts.reduce((sum, d) => (d?.discountType === 'Qbeek' ? sum + value(d) : sum), 0);
    discount = cents(all - Math.min(bonus, loyalty));
  } else {
    discount = cents(num(firstDefined(order?.discountAmount, order?.appliedDiscount, order?.totalDiscount)));
  }

  const net = serverNet != null ? cents(serverNet) : cents(Math.max(0, gross - discount - bonus));
  return { gross, discount, bonus, net, hasServerNet: serverNet != null };
}
